import { queryOptions, useQuery, keepPreviousData } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import {
  getUsageMetrics,
  getCurationMetrics,
  getExtractionOutputMetrics,
  getSubstanceVerificationMetrics,
  getExtractionHealthMetrics,
  getExtractionEvents,
  getFieldChangeDetails,
  getFieldValueChanges,
} from './api';
import type { AnalyticsFilters } from '@/api/types';
import type { CurationChangeKind } from '@/api/generated-types';

const ANALYTICS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

export const usageQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.usage(filters),
    queryFn: () => getUsageMetrics(filters),
    staleTime: ANALYTICS_STALE_TIME,
  });

export const curationQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.curation(filters),
    queryFn: () => getCurationMetrics(filters),
    staleTime: ANALYTICS_STALE_TIME,
  });

export const extractionOutputQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.extractionOutput(filters),
    queryFn: () => getExtractionOutputMetrics(filters),
    staleTime: ANALYTICS_STALE_TIME,
  });

export const substanceValidationQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.substanceValidation(filters),
    queryFn: () => getSubstanceVerificationMetrics(filters),
    staleTime: ANALYTICS_STALE_TIME,
  });

export const extractionHealthQueryOptions = (filters: AnalyticsFilters) =>
  queryOptions({
    queryKey: queryKeys.analytics.extractionHealth(filters),
    queryFn: () => getExtractionHealthMetrics(filters),
    staleTime: ANALYTICS_STALE_TIME,
  });

export function useUsageMetrics(filters: AnalyticsFilters) {
  return useQuery({ ...usageQueryOptions(filters), placeholderData: keepPreviousData });
}

export function useCurationMetrics(filters: AnalyticsFilters) {
  return useQuery({ ...curationQueryOptions(filters), placeholderData: keepPreviousData });
}

export function useExtractionOutputMetrics(filters: AnalyticsFilters) {
  return useQuery({ ...extractionOutputQueryOptions(filters), placeholderData: keepPreviousData });
}

export function useSubstanceVerificationMetrics(filters: AnalyticsFilters) {
  return useQuery({ ...substanceValidationQueryOptions(filters), placeholderData: keepPreviousData });
}

export function useExtractionHealthMetrics(filters: AnalyticsFilters) {
  return useQuery({ ...extractionHealthQueryOptions(filters), placeholderData: keepPreviousData });
}

export function useExtractionEvents(
  filters: AnalyticsFilters,
  eventType: string | null,
  messageFilter: string | null,
  page: number = 1
) {
  return useQuery({
    queryKey: queryKeys.analytics.extractionEvents(filters, eventType, messageFilter, page),
    queryFn: () => getExtractionEvents(filters, eventType, messageFilter, page),
    enabled: !!eventType || !!messageFilter,
    staleTime: ANALYTICS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useFieldChangeDetails(
  fieldKey: string | null,
  filters: AnalyticsFilters,
  page: number = 1
) {
  return useQuery({
    queryKey: queryKeys.analytics.curationFieldChanges(fieldKey ?? '', filters, page),
    queryFn: () => getFieldChangeDetails(fieldKey!, filters, page),
    enabled: !!fieldKey,
    staleTime: ANALYTICS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useFieldValueChanges(
  fieldKey: string | null,
  filters: AnalyticsFilters,
  changeKind?: CurationChangeKind
) {
  return useQuery({
    queryKey: queryKeys.analytics.curationFieldValueChanges(fieldKey ?? '', filters, changeKind),
    queryFn: () => getFieldValueChanges(fieldKey!, filters, changeKind),
    enabled: !!fieldKey,
    staleTime: ANALYTICS_STALE_TIME,
    placeholderData: keepPreviousData,
  });
}
